import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { IRequestInfo } from 'legacy/lut/classes/models/service'
import { RequestStatus } from 'legacy/lut/configs/service'

import { IPartnerSearchQueryParams } from '../../models/search'
import { IContactIndex } from '../../services/search_v2/models/contact/contactIndex'
import { IPartnerIndex } from '../../services/search_v2/models/partner/partnerIndex'

import { requestMorePartners } from './api'
import { requestMoreContacts } from './api/requestMoreContacts'
import { initialState } from './configs'
import { PARTNER_SEARCH_NAMESPACE } from './constants'
import { getHighlightedWordsFromFilters } from './helpers/getHighlightedWordsFromFilters'

// @Deprecated: Use `PartnerSearch` from `clickNgo/features/partnerSearch`
export const partnerSearchSlice = createSlice({
  name: PARTNER_SEARCH_NAMESPACE,
  initialState: initialState,
  reducers: {
    initPartnerSearchFilters: (
      state,
      action: PayloadAction<{
        filters: IPartnerSearchQueryParams
        isP360?: boolean
        isElasticSearch?: boolean
      }>
    ) => {
      state.filters = action.payload.filters
      state.isP360 = action.payload.isP360
      state.isElasticSearch = action.payload.isElasticSearch
    },
    updatePartnerSearchFilters: (
      state,
      action: PayloadAction<Partial<IPartnerSearchQueryParams>>
    ) => {
      state.filters = { ...state.filters, ...action.payload }
      state.filters.pagination.partnersPage = 1
      state.filters.pagination.contactsPage = 1
      state.highlightedWords = getHighlightedWordsFromFilters(state.filters)
    },
    updatePartnerSearchRequestInfo: (state, action: PayloadAction<IRequestInfo>) => {
      state.message = action.payload.message
      state.meta = action.payload.meta
      state.status = action.payload.status
    },
    updateSearchResults: (
      state,
      action: PayloadAction<{ partners: IPartnerIndex[]; contacts: IContactIndex[] }>
    ) => {
      const { partners, contacts } = action.payload
      state.partners.data = partners
      state.contacts.data = contacts
      state.partners.canLoadMore = partners.length > 0
      state.contacts.canLoadMore = contacts.length > 0
    },
    updatePartnersDataWithProgramCodes: (state, action: PayloadAction<IPartnerIndex[]>) => {
      state.partners.data = action.payload
    },
    resetPartnerSearchFilters: (
      state,
      action: PayloadAction<IPartnerSearchQueryParams | undefined>
    ) => ({
      ...initialState,
      filters: action.payload ?? initialState.filters,
      isP360: state.isP360,
      isElasticSearch: state.isElasticSearch,
    }),
  },
  extraReducers: (builder) =>
    builder
      .addCase(requestMorePartners.pending, (state) => {
        state.filters.pagination.partnersPage += 1
        state.status = RequestStatus.PENDING
      })
      .addCase(requestMorePartners.rejected, (state) => {
        state.status = RequestStatus.REJECTED
        state.message = 'Unable to load more search results'
      })
      .addCase(requestMorePartners.fulfilled, (state, action) => {
        state.status = RequestStatus.FULFILLED
        state.message = ''
        const partners = action.payload
        state.partners.data = [...state.partners.data, ...partners]
        state.partners.canLoadMore = partners.length > 0
      })
      .addCase(requestMoreContacts.pending, (state) => {
        state.filters.pagination.contactsPage += 1
        state.status = RequestStatus.PENDING
      })
      .addCase(requestMoreContacts.rejected, (state) => {
        state.status = RequestStatus.REJECTED
        state.message = 'Unable to load more search results'
      })
      .addCase(requestMoreContacts.fulfilled, (state, action) => {
        state.status = RequestStatus.FULFILLED
        state.message = ''
        const contacts = action.payload
        state.contacts.data = [...state.contacts.data, ...contacts]
        state.contacts.canLoadMore = contacts.length > 0
      }),
})

export const {
  initPartnerSearchFilters,
  updatePartnerSearchFilters,
  updatePartnerSearchRequestInfo,
  resetPartnerSearchFilters,
  updateSearchResults,
  updatePartnersDataWithProgramCodes,
} = partnerSearchSlice.actions

export const partnerSearchReducer = partnerSearchSlice.reducer
